import { FormEvent, useEffect, useRef, useState } from "react";
import {
  createFileRoute,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import {
  Cog6ToothIcon,
  MicrophoneIcon,
  PencilSquareIcon,
  SpeakerWaveIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import useTranslation from "../../hooks/useTranslation";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { fetchSpeakingPracticeSpeech } from "../../hooks/useSpeakingPracticeSpeech";
import {
  useSpeakingPracticeReply,
  type SpeakingPracticeMessage,
} from "../../hooks/useSpeakingPracticeReply";
import { useLanguageSpace } from "../../hooks/useLanguageSpace";
import {
  maybeConsumeSpeakingTriggerCue,
  popActiveSpeakingTrigger,
  type SpeakingTriggerItem,
} from "../../hooks/useSpeakingTriggers";

type SpeechRecognitionEvent = Event & {
  resultIndex?: number;
  results: ArrayLike<{
    isFinal?: boolean;
    length?: number;
    [index: number]: {
      transcript: string;
      confidence?: number;
    };
  }>;
};

type SpeechRecognitionInstance = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  maxAlternatives?: number;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: Event) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
};

declare global {
  interface Window {
    SpeechRecognition?: new () => SpeechRecognitionInstance;
    webkitSpeechRecognition?: new () => SpeechRecognitionInstance;
    webkitAudioContext?: typeof AudioContext;
  }
}

export const Route = createFileRoute("/app/dialogue")({
  component: Dialogue,
});

type DialogueMessage = SpeakingPracticeMessage & {
  correction?: string;
  coaching?: {
    better_phrasing: string;
    pronunciation_focus: string;
    new_word: string;
  };
};

function Dialogue() {
  const { translate, language: appLanguage } = useTranslation();
  const navigate = useNavigate();
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const isDialogueRouteActive = pathname.startsWith("/app/dialogue");
  const replyMutation = useSpeakingPracticeReply();
  const { languageSpace } = useLanguageSpace("ENGLISH");
  const [input, setInput] = useState("");
  const inputRef = useRef("");
  const [messages, setMessages] = useState<DialogueMessage[]>([]);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [micError, setMicError] = useState("");
  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);
  const finalTranscriptRef = useRef("");
  const finalTranscriptByIndexRef = useRef<Record<number, string>>({});
  const finalTranscriptLastChangedAtRef = useRef(0);
  const silenceTimerRef = useRef<number | null>(null);
  const onEndTimerRef = useRef<number | null>(null);
  const retryTimerRef = useRef<number | null>(null);
  const localeSwitchCooldownRef = useRef(0);
  const localeSwitchInFlightRef = useRef(false);
  const manualStopRef = useRef(false);
  const shouldKeepListeningRef = useRef(false);
  const isListeningRef = useRef(false);
  const isPendingRef = useRef(false);
  const messagesRef = useRef<DialogueMessage[]>([]);
  const [expandedCorrections, setExpandedCorrections] = useState<
    Record<number, boolean>
  >({});
  const [micApiSupported, setMicApiSupported] = useState(false);
  const [isFirefox, setIsFirefox] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioUrlRef = useRef<string | null>(null);
  const hasWelcomedRef = useRef(false);
  const [voiceError, setVoiceError] = useState("");
  const [pendingVoiceReplayText, setPendingVoiceReplayText] = useState("");
  const [voiceSpeed, setVoiceSpeed] = useState<"normal" | "slow">("normal");
  const [autoVoiceEnabled, setAutoVoiceEnabled] = useLocalStorage<boolean>(
    "speaking-auto-voice-v1",
    true,
  );
  const [micMode, setMicMode] = useState<"toggle" | "hold">("toggle");
  const [handsFreeMode, setHandsFreeMode] = useState(true);
  const [micLanguageMode, setMicLanguageMode] = useState<
    "auto" | "learning" | "german"
  >("auto");
  const [showVoiceSettings, setShowVoiceSettings] = useState(false);
  const [pauseSensitivity, setPauseSensitivity] = useState<
    "fast" | "balanced" | "patient"
  >("balanced");
  const [speakingCueMode, setSpeakingCueMode] = useLocalStorage<
    "off" | "subtle" | "prominent"
  >("speaking-cue-mode-v1", "subtle");
  const [coachingEnabled, setCoachingEnabled] = useLocalStorage<boolean>(
    "speaking-coaching-enabled-v1",
    false,
  );
  const [walkingModeEnabled, setWalkingModeEnabled] = useLocalStorage<boolean>(
    "speaking-walking-mode-v1",
    false,
  );
  const coachingEnabledRef = useRef(coachingEnabled);
  const walkingModeEnabledRef = useRef(walkingModeEnabled);
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);
  const activeRecognitionLanguageRef = useRef(languageSpace);
  const [isTextInputOpen, setIsTextInputOpen] = useState(false);
  const [lastTranscript, setLastTranscript] = useState("");
  const [activeTrigger, setActiveTrigger] =
    useState<SpeakingTriggerItem | null>(null);
  const [cueSnoozedForSession, setCueSnoozedForSession] = useState(false);
  const [isIOSDevice, setIsIOSDevice] = useState(false);
  const [isDesktopDevice, setIsDesktopDevice] = useState(false);
  const hasMicPermissionRef = useRef(false);
  const transcriptViewportRef = useRef<HTMLDivElement | null>(null);
  const audioCueContextRef = useRef<AudioContext | null>(null);
  const speechRecognitionLanguageMap: Record<string, string> = {
    ENGLISH: "en-US",
    FRENCH: "fr-FR",
    GERMAN: "de-DE",
    SPANISH: "es-ES",
    ITALIAN: "it-IT",
    PORTUGUESE: "pt-PT",
    PORTUGUESE_FROM_PORTUGAL: "pt-PT",
    PORTUGUESE_FROM_BRAZIL: "pt-BR",
    RUSSIAN: "ru-RU",
    GREEK: "el-GR",
  };

  const logSpeakingTelemetry = (
    event: string,
    payload?: Record<string, unknown>,
  ) => {
    if (typeof console === "undefined") {
      return;
    }
    console.log("[speaking-telemetry]", event, payload || {});
  };

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    if (typeof navigator === "undefined") {
      return;
    }
    const ua = navigator.userAgent || "";
    setIsFirefox(/firefox/i.test(ua) && !/seamonkey/i.test(ua));
    const isiOS = /iPad|iPhone|iPod/.test(ua);
    setIsIOSDevice(isiOS);
    const isMobileUa = /Android|iPhone|iPad|iPod/i.test(ua);
    setIsDesktopDevice(!isMobileUa);
    if (
      isiOS &&
      typeof window !== "undefined" &&
      window.localStorage.getItem("speaking-auto-voice-v1") === null
    ) {
      setAutoVoiceEnabled(false);
    }
  }, [setAutoVoiceEnabled]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    setCueSnoozedForSession(
      window.sessionStorage.getItem("speaking-cue-snooze-session-v1") === "1",
    );
  }, []);

  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.permissions?.query) {
      return;
    }
    let mounted = true;
    let permissionStatus: PermissionStatus | null = null;
    const sync = () => {
      if (!mounted || !permissionStatus) {
        return;
      }
      hasMicPermissionRef.current = permissionStatus.state === "granted";
    };
    navigator.permissions
      .query({ name: "microphone" as PermissionName })
      .then((status) => {
        permissionStatus = status;
        sync();
        logSpeakingTelemetry("mic_permission_state", { state: status.state });
        permissionStatus.onchange = sync;
      })
      .catch(() => {
        // noop
      });
    return () => {
      mounted = false;
      if (permissionStatus) {
        permissionStatus.onchange = null;
      }
    };
  }, []);

  useEffect(() => {
    inputRef.current = input;
  }, [input]);

  useEffect(() => {
    coachingEnabledRef.current = coachingEnabled;
  }, [coachingEnabled]);

  useEffect(() => {
    walkingModeEnabledRef.current = walkingModeEnabled;
  }, [walkingModeEnabled]);

  const pulseWalkingFeedback = (pattern: number | number[]) => {
    if (!walkingModeEnabledRef.current || typeof navigator === "undefined") {
      return;
    }
    try {
      navigator.vibrate?.(pattern);
    } catch {
      // noop
    }
  };

  const playWalkingCue = (kind: "listening" | "sent" | "error") => {
    if (!walkingModeEnabledRef.current || typeof window === "undefined") {
      return;
    }
    const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextCtor) {
      return;
    }
    try {
      const context = audioCueContextRef.current ?? new AudioContextCtor();
      audioCueContextRef.current = context;
      const now = context.currentTime;
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      const frequency =
        kind === "listening" ? 880 : kind === "sent" ? 660 : 220;
      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(frequency, now);
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.05, now + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.16);
      oscillator.connect(gain);
      gain.connect(context.destination);
      oscillator.start(now);
      oscillator.stop(now + 0.18);
    } catch {
      // Audio cues are best-effort.
    }
  };

  const requestWalkingWakeLock = async () => {
    if (typeof navigator === "undefined" || !navigator.wakeLock) {
      return;
    }
    try {
      wakeLockRef.current = await navigator.wakeLock.request("screen");
      wakeLockRef.current.addEventListener("release", () => {
        wakeLockRef.current = null;
      });
      logSpeakingTelemetry("walking_wake_lock_acquired");
    } catch {
      logSpeakingTelemetry("walking_wake_lock_failed");
    }
  };

  const releaseWalkingWakeLock = () => {
    const wakeLock = wakeLockRef.current;
    wakeLockRef.current = null;
    if (!wakeLock) {
      return;
    }
    void wakeLock.release().catch(() => {
      // noop
    });
  };

  useEffect(() => {
    if (!walkingModeEnabled || !isDialogueRouteActive) {
      releaseWalkingWakeLock();
      return;
    }
    void requestWalkingWakeLock();
    const onVisibilityChange = () => {
      if (
        document.visibilityState === "visible" &&
        walkingModeEnabledRef.current &&
        !wakeLockRef.current
      ) {
        void requestWalkingWakeLock();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [isDialogueRouteActive, walkingModeEnabled]);

  useEffect(() => {
    const trigger =
      popActiveSpeakingTrigger() ?? maybeConsumeSpeakingTriggerCue();
    if (trigger) {
      setActiveTrigger(trigger);
    }
  }, []);

  useEffect(() => {
    if (!transcriptViewportRef.current) {
      return;
    }
    transcriptViewportRef.current.scrollTop =
      transcriptViewportRef.current.scrollHeight;
  }, [messages]);

  useEffect(() => {
    isListeningRef.current = isListening;
  }, [isListening]);

  useEffect(() => {
    isPendingRef.current = replyMutation.isPending;
  }, [replyMutation.isPending]);

  const resolveSpeechRecognitionCtor = () => {
    if (typeof window === "undefined") {
      return null;
    }
    const win = window as Window & {
      SpeechRecognition?: unknown;
      webkitSpeechRecognition?: unknown;
    };
    const candidates = [
      win.SpeechRecognition,
      win.webkitSpeechRecognition,
      (globalThis as { SpeechRecognition?: unknown }).SpeechRecognition,
      (globalThis as { webkitSpeechRecognition?: unknown })
        .webkitSpeechRecognition,
    ];
    const ctor = candidates.find(
      (candidate) => typeof candidate === "function",
    );
    return (ctor as (new () => SpeechRecognitionInstance) | undefined) ?? null;
  };

  const playBrowserSpeechFallback = (text: string) => {
    if (
      typeof window === "undefined" ||
      !("speechSynthesis" in window) ||
      !text.trim()
    ) {
      return false;
    }
    try {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = speechRecognitionLanguageMap[languageSpace] || "en-US";
      utterance.rate = voiceSpeed === "slow" ? 0.82 : 0.95;
      utterance.onend = () => {
        setIsSpeaking(false);
        if (
          handsFreeMode &&
          hasMicPermissionRef.current &&
          isDialogueRouteActive
        ) {
          startListeningIfPossible();
        }
      };
      utterance.onerror = () => {
        setIsSpeaking(false);
        playWalkingCue("error");
        pulseWalkingFeedback([40, 40, 40]);
      };
      setIsSpeaking(true);
      window.speechSynthesis.speak(utterance);
      return true;
    } catch {
      return false;
    }
  };

  const speakText = async (
    text: string,
    options?: { suppressErrorUi?: boolean },
  ) => {
    if (
      typeof window === "undefined" ||
      !text.trim() ||
      !isDialogueRouteActive
    ) {
      return;
    }
    setVoiceError("");
    setPendingVoiceReplayText("");
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }
    try {
      setIsSpeaking(true);
      const audioBlob = await fetchSpeakingPracticeSpeech({
        text,
        language: languageSpace,
        speed: voiceSpeed,
      });
      const url = URL.createObjectURL(audioBlob);
      audioUrlRef.current = url;
      const audio = new Audio(url);
      audio.playbackRate = voiceSpeed === "slow" ? 0.85 : 1;
      if (
        isDesktopDevice &&
        typeof (audio as { setSinkId?: (id: string) => Promise<void> })
          .setSinkId === "function"
      ) {
        try {
          await (
            audio as { setSinkId: (id: string) => Promise<void> }
          ).setSinkId("default");
          logSpeakingTelemetry("tts_sink_default_applied");
        } catch {
          logSpeakingTelemetry("tts_sink_default_failed");
        }
      }
      audioRef.current = audio;
      audio.onended = () => {
        setIsSpeaking(false);
        if (
          handsFreeMode &&
          hasMicPermissionRef.current &&
          isDialogueRouteActive
        ) {
          startListeningIfPossible();
        }
      };
      audio.onerror = () => {
        setIsSpeaking(false);
        logSpeakingTelemetry("tts_playback_error");
        if (playBrowserSpeechFallback(text)) {
          return;
        }
        playWalkingCue("error");
        pulseWalkingFeedback([40, 40, 40]);
        setVoiceError(
          translate("speaking_practice_tts_error") ||
            "Voice playback is not available right now.",
        );
      };
      await audio.play();
    } catch (error) {
      setIsSpeaking(false);
      const isAutoplayBlocked =
        error instanceof DOMException &&
        (error.name === "NotAllowedError" || error.name === "AbortError");
      logSpeakingTelemetry("tts_playback_exception", {
        errorName: error instanceof Error ? error.name : "unknown",
        autoplayBlocked: isAutoplayBlocked,
      });
      if (isAutoplayBlocked) {
        setPendingVoiceReplayText(text);
        setVoiceError(
          translate("speaking_practice_tts_autoplay_blocked") ||
            "Voice is ready, but playback was blocked by browser autoplay rules. Tap once to play.",
        );
        return;
      }
      if (!options?.suppressErrorUi) {
        if (playBrowserSpeechFallback(text)) {
          return;
        }
        playWalkingCue("error");
        pulseWalkingFeedback([40, 40, 40]);
        setPendingVoiceReplayText(text);
        setVoiceError(
          translate("speaking_practice_tts_error") ||
            "Voice playback is not available right now.",
        );
      }
    }
  };

  const clearSilenceTimer = () => {
    if (silenceTimerRef.current !== null && typeof window !== "undefined") {
      window.clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  };

  const clearOnEndTimer = () => {
    if (onEndTimerRef.current !== null && typeof window !== "undefined") {
      window.clearTimeout(onEndTimerRef.current);
      onEndTimerRef.current = null;
    }
  };
  const clearRetryTimer = () => {
    if (retryTimerRef.current !== null && typeof window !== "undefined") {
      window.clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  };

  const collapseImmediateRepeatedWords = (text: string) => {
    const tokens = text.split(/\s+/).filter(Boolean);
    if (tokens.length < 2) {
      return text.trim();
    }
    const compact: string[] = [];
    for (const token of tokens) {
      const last = compact[compact.length - 1];
      if (last && last.toLowerCase() === token.toLowerCase()) {
        continue;
      }
      compact.push(token);
    }
    return compact.join(" ").trim();
  };

  const startListeningIfPossible = () => {
    const recognition = recognitionRef.current;
    if (!recognition || isListeningRef.current || isPendingRef.current) {
      return;
    }
    finalTranscriptRef.current = "";
    finalTranscriptByIndexRef.current = {};
    manualStopRef.current = false;
    shouldKeepListeningRef.current = true;
    try {
      recognition.start();
      setIsListening(true);
      playWalkingCue("listening");
      pulseWalkingFeedback(30);
    } catch {
      // noop - browser may reject rapid restart
    }
  };

  const computePauseMs = (words: number) => {
    const longSentenceBuffer =
      words >= 20 ? 1400 : words >= 14 ? 1000 : words >= 10 ? 700 : 0;
    if (pauseSensitivity === "fast") {
      return (words < 4 ? 1000 : 700) + longSentenceBuffer;
    }
    if (pauseSensitivity === "patient") {
      return (words < 4 ? 3200 : 2200) + longSentenceBuffer;
    }
    return (words < 4 ? 1500 : 1000) + longSentenceBuffer;
  };

  const computeOnEndGraceMs = (words: number) => {
    const longSentenceBuffer =
      words >= 20 ? 1200 : words >= 14 ? 850 : words >= 10 ? 550 : 0;
    if (pauseSensitivity === "fast") {
      return (words < 4 ? 1200 : 850) + longSentenceBuffer;
    }
    if (pauseSensitivity === "patient") {
      return (words < 4 ? 3600 : 2400) + longSentenceBuffer;
    }
    return (words < 4 ? 1900 : 1200) + longSentenceBuffer;
  };

  const computeFinalStabilityMs = (words: number) => {
    if (pauseSensitivity === "fast") {
      return words < 4 ? 520 : 360;
    }
    if (pauseSensitivity === "patient") {
      return words < 4 ? 900 : 650;
    }
    return words < 4 ? 700 : 500;
  };

  const resolveMicLocale = () => {
    if (micLanguageMode === "german") {
      return "de-DE";
    }
    if (micLanguageMode === "auto") {
      return (
        speechRecognitionLanguageMap[activeRecognitionLanguageRef.current] ||
        speechRecognitionLanguageMap[languageSpace] ||
        "en-US"
      );
    }
    return speechRecognitionLanguageMap[languageSpace] || "en-US";
  };

  const setRecognitionLanguageSpace = (nextLanguageSpace: string) => {
    activeRecognitionLanguageRef.current = nextLanguageSpace;
    const recognition = recognitionRef.current;
    const nextLocale = speechRecognitionLanguageMap[nextLanguageSpace];
    if (recognition && nextLocale) {
      recognition.lang = nextLocale;
    }
  };

  const extractMicLanguageCommand = (text: string): string | null => {
    const normalized = text.trim().toLowerCase();
    if (!normalized) {
      return null;
    }
    const commandPatterns: Array<[string, RegExp[]]> = [
      ["AUTO", [/\bmic(rophone)?\s+auto\b/, /\bmikro\s+auto\b/]],
      [
        "LEARNING",
        [
          /\bmic(rophone)?\s+(learning|target)\b/,
          /\bmikro\s+(lernsprache|zielsprache)\b/,
        ],
      ],
      ["GERMAN", [/\bmic(rophone)?\s+german\b/, /\bmikro\s+deutsch\b/]],
      ["ENGLISH", [/\bmic(rophone)?\s+english\b/, /\bmikro\s+englisch\b/]],
      ["FRENCH", [/\bmic(rophone)?\s+french\b/, /\bmikro\s+franz[öo]sisch\b/]],
      ["SPANISH", [/\bmic(rophone)?\s+spanish\b/, /\bmikro\s+spanisch\b/]],
      ["ITALIAN", [/\bmic(rophone)?\s+italian\b/, /\bmikro\s+italienisch\b/]],
      [
        "PORTUGUESE",
        [/\bmic(rophone)?\s+portuguese\b/, /\bmikro\s+portugiesisch\b/],
      ],
      ["RUSSIAN", [/\bmic(rophone)?\s+russian\b/, /\bmikro\s+russisch\b/]],
      ["GREEK", [/\bmic(rophone)?\s+greek\b/, /\bmikro\s+griechisch\b/]],
    ];
    for (const [language, patterns] of commandPatterns) {
      if (patterns.some((pattern) => pattern.test(normalized))) {
        return language;
      }
    }
    return null;
  };

  const applyMicLanguageCommand = (command: string) => {
    if (command === "AUTO") {
      setMicLanguageMode("auto");
      setRecognitionLanguageSpace(languageSpace);
      return translate("speaking_practice_mic_command_auto") || "Mic auto.";
    }
    if (command === "LEARNING") {
      setMicLanguageMode("learning");
      setRecognitionLanguageSpace(languageSpace);
      return (
        translate("speaking_practice_mic_command_learning") ||
        "Mic set to learning language."
      );
    }
    if (command === "GERMAN") {
      setMicLanguageMode("german");
    } else {
      setMicLanguageMode("auto");
    }
    setRecognitionLanguageSpace(command);
    const languageLabel =
      command.charAt(0).toUpperCase() + command.slice(1).toLowerCase();
    return `${translate("speaking_practice_mic_command_set") || "Mic set to"} ${languageLabel}.`;
  };

  const rescueUtterancePatterns = [
    /\b(ich\s+verstehe)\b/i,
    /\b(ich\s+habe\s+nicht\s+verstanden)\b/i,
    /\b(ich\s+check(e)?\s+nicht)\b/i,
    /\b(ich\s+bin\s+verloren)\b/i,
    /\b(was\s+heißt)\b/i,
    /\b(was\s+bedeutet)\b/i,
    /\b(wiederhole)\b/i,
    /\b(nochmal)\b/i,
    /\b(langsam)\b/i,
    /\b(bit(te)?\s+langsam)\b/i,
    /\b(auf\s+deutsch)\b/i,
    /\b(in\s+deutsch)\b/i,
    /\b(in\s+german)\b/i,
    /\b(in\s+english)\b/i,
    /\b(i\s+don't\s+understand)\b/i,
    /\b(can\s+you\s+repeat)\b/i,
    /\b(en\s+allemand)\b/i,
    /\b(en\s+anglais)\b/i,
    /\b(je\s+ne\s+comprends\s+pas)\b/i,
  ];

  const isLikelyGermanRescueUtterance = (text: string) => {
    const normalized = text.trim();
    if (!normalized) {
      return false;
    }
    return rescueUtterancePatterns.some((pattern) => pattern.test(normalized));
  };

  const guessLanguageSpaceFromTranscript = (text: string): string | null => {
    const normalized = text.trim().toLowerCase();
    if (!normalized) {
      return null;
    }
    if (/[а-яё]/i.test(normalized)) {
      return "RUSSIAN";
    }
    if (/[α-ωάέήίόύώ]/i.test(normalized)) {
      return "GREEK";
    }
    const tokens = normalized.split(/[\s,.;:!?'"()-]+/).filter(Boolean);
    if (tokens.length < 2) {
      return null;
    }
    const score = {
      FRENCH: 0,
      GERMAN: 0,
      ENGLISH: 0,
      SPANISH: 0,
      ITALIAN: 0,
      PORTUGUESE: 0,
    };
    const tokenSet = new Set(tokens);
    const bump = (lang: keyof typeof score, points: number) => {
      score[lang] += points;
    };

    [
      "je",
      "j'",
      "vous",
      "nous",
      "est",
      "pas",
      "avec",
      "bonjour",
      "merci",
    ].forEach((w) => tokenSet.has(w) && bump("FRENCH", 2));
    [
      "ich",
      "bin",
      "nicht",
      "und",
      "der",
      "die",
      "das",
      "mit",
      "hallo",
      "danke",
    ].forEach((w) => tokenSet.has(w) && bump("GERMAN", 2));
    ["i", "you", "the", "and", "with", "hello", "thanks", "please"].forEach(
      (w) => tokenSet.has(w) && bump("ENGLISH", 2),
    );
    ["yo", "hola", "gracias", "por", "que", "con", "estoy", "puedo"].forEach(
      (w) => tokenSet.has(w) && bump("SPANISH", 2),
    );
    ["ciao", "grazie", "con", "sono", "posso", "per", "che"].forEach(
      (w) => tokenSet.has(w) && bump("ITALIAN", 2),
    );
    ["olá", "obrigado", "obrigada", "com", "que", "estou", "posso"].forEach(
      (w) => tokenSet.has(w) && bump("PORTUGUESE", 2),
    );
    if (/[àâçéèêëîïôùûüÿœ]/i.test(normalized)) bump("FRENCH", 2);
    if (/[äöüß]/i.test(normalized)) bump("GERMAN", 3);
    if (/[ñ¡¿]/i.test(normalized)) bump("SPANISH", 2);
    if (/[ãõ]/i.test(normalized)) bump("PORTUGUESE", 2);

    const ranked = Object.entries(score).sort((a, b) => b[1] - a[1]);
    const best = ranked[0];
    const second = ranked[1];
    if (!best || best[1] < 3) {
      return null;
    }
    if (second && best[1] - second[1] < 2) {
      return null;
    }
    return best[0];
  };

  const maybeSwitchRecognitionLocale = (text: string) => {
    const recognition = recognitionRef.current;
    if (
      !recognition ||
      !isListeningRef.current ||
      localeSwitchInFlightRef.current
    ) {
      return;
    }
    const now = Date.now();
    if (now < localeSwitchCooldownRef.current) {
      return;
    }
    const guessedSpace = guessLanguageSpaceFromTranscript(text);
    if (!guessedSpace) {
      return;
    }
    const targetLocale = speechRecognitionLanguageMap[guessedSpace];
    if (!targetLocale || recognition.lang === targetLocale) {
      return;
    }
    localeSwitchInFlightRef.current = true;
    shouldKeepListeningRef.current = true;
    localeSwitchCooldownRef.current = now + 2200;
    activeRecognitionLanguageRef.current = guessedSpace;
    recognition.lang = targetLocale;
    try {
      recognition.stop();
    } catch {
      localeSwitchInFlightRef.current = false;
    }
  };

  const pickBestTranscriptForAutoMode = (
    result: SpeechRecognitionEvent["results"][number],
  ) => {
    const count = typeof result.length === "number" ? result.length : 1;
    let best = (result[0]?.transcript || "").trim();
    let bestScore = -1000;
    for (let i = 0; i < count; i += 1) {
      const candidate = (result[i]?.transcript || "").trim();
      if (!candidate) {
        continue;
      }
      const guessed = guessLanguageSpaceFromTranscript(candidate);
      let score = 0;
      if (isLikelyGermanRescueUtterance(candidate)) {
        score += 8;
      }
      if (guessed === "GERMAN") {
        score += 5;
      }
      if (guessed === languageSpace) {
        score += 4;
      }
      if (
        languageSpace !== "GERMAN" &&
        guessed === "GERMAN" &&
        !isLikelyGermanRescueUtterance(candidate)
      ) {
        score -= 4;
      }
      if (languageSpace === "GERMAN" && guessed === "GERMAN") {
        score += 2;
      }
      if (candidate.length > 6) {
        score += 0.4;
      }
      if (score > bestScore) {
        bestScore = score;
        best = candidate;
      }
    }
    return best;
  };

  const sendUserTurn = async (rawText: string) => {
    const trimmed = collapseImmediateRepeatedWords(rawText.trim());
    if (!trimmed || isPendingRef.current) {
      return;
    }
    if (walkingModeEnabledRef.current) {
      const micLanguageCommand = extractMicLanguageCommand(trimmed);
      if (micLanguageCommand) {
        const confirmation = applyMicLanguageCommand(micLanguageCommand);
        const updated = [
          ...messagesRef.current,
          { role: "assistant" as const, text: confirmation },
        ];
        setMessages(updated);
        messagesRef.current = updated;
        playWalkingCue("sent");
        pulseWalkingFeedback(20);
        if (autoVoiceEnabled) {
          if (!playBrowserSpeechFallback(confirmation)) {
            void speakText(confirmation, { suppressErrorUi: true });
          }
        }
        if (!isListeningRef.current && !isPendingRef.current) {
          window.setTimeout(() => startListeningIfPossible(), 250);
        }
        return;
      }
    }
    const previousMessages = messagesRef.current.slice(-8);
    const nextMessages = [
      ...messagesRef.current,
      { role: "user" as const, text: trimmed },
    ];
    setMessages(nextMessages);
    messagesRef.current = nextMessages;
    setInput("");
    finalTranscriptRef.current = "";
    finalTranscriptByIndexRef.current = {};
    setLastTranscript(trimmed);
    playWalkingCue("sent");
    pulseWalkingFeedback(20);

    try {
      const payload = await replyMutation.mutateAsync({
        message: trimmed,
        recentMessages: previousMessages,
        coachingEnabled: coachingEnabledRef.current,
        languageSpace,
        focusWord: activeTrigger?.word || "",
        focusMnemonic: activeTrigger?.mnemonic || "",
        triggerReason: activeTrigger?.reason || "",
      });
      const correctedUserMessages = nextMessages.map((item, index) => {
        if (index !== nextMessages.length - 1 || item.role !== "user") {
          return item;
        }
        return {
          ...item,
          correction: (payload.correction || "").trim(),
        };
      });
      const updated = [
        ...correctedUserMessages,
        {
          role: "assistant" as const,
          text: payload.reply,
          coaching: payload.coaching,
        },
      ];
      setMessages(updated);
      messagesRef.current = updated;
      if (autoVoiceEnabled) {
        void speakText(payload.reply || "");
      }
    } catch {
      playWalkingCue("error");
      pulseWalkingFeedback([40, 40, 40]);
      const errorText =
        translate("speaking_practice_error") ||
        "That did not work right now. Please try again.";
      const updated = [
        ...nextMessages,
        { role: "assistant" as const, text: errorText },
      ];
      setMessages(updated);
      messagesRef.current = updated;
      if (autoVoiceEnabled) {
        void speakText(errorText);
      }
    }
  };

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    if (!isDialogueRouteActive) {
      return;
    }
    const SpeechRecognitionCtor = resolveSpeechRecognitionCtor();
    if (SpeechRecognitionCtor) {
      try {
        const recognition = new SpeechRecognitionCtor();
        recognition.continuous = true;
        recognition.interimResults = true;
        setRecognitionLanguageSpace(
          micLanguageMode === "german" ? "GERMAN" : languageSpace,
        );
        recognition.lang = resolveMicLocale();
        recognition.maxAlternatives = 5;
        recognition.onresult = (event) => {
          const resultIndex =
            typeof event.resultIndex === "number" ? event.resultIndex : 0;
          let interimCombined = "";
          for (let i = 0; i < event.results.length; i += 1) {
            const part =
              micLanguageMode === "auto"
                ? pickBestTranscriptForAutoMode(event.results[i])
                : event.results[i][0].transcript;
            if ((event.results[i] as { isFinal?: boolean }).isFinal) {
              finalTranscriptByIndexRef.current[i] = part;
            } else if (i >= resultIndex) {
              interimCombined += `${part} `;
            }
          }
          const stableFinal = Object.keys(finalTranscriptByIndexRef.current)
            .map((value) => Number(value))
            .sort((a, b) => a - b)
            .map((index) => finalTranscriptByIndexRef.current[index])
            .join(" ")
            .trim();
          if (stableFinal !== finalTranscriptRef.current) {
            finalTranscriptLastChangedAtRef.current = Date.now();
          }
          finalTranscriptRef.current = stableFinal;
          const combined = `${stableFinal} ${interimCombined}`.trim();
          setInput(combined);

          clearSilenceTimer();
          clearOnEndTimer();
          if (isListeningRef.current && !isPendingRef.current) {
            const words = combined.trim().split(/\s+/).filter(Boolean).length;
            const silenceMs = computePauseMs(words);
            silenceTimerRef.current = window.setTimeout(() => {
              const finalCandidate = finalTranscriptRef.current.trim();
              const interimCandidate = combined.trim();
              // Do not cut the user off on unstable interim text.
              if (
                !finalCandidate &&
                interimCandidate.split(/\s+/).filter(Boolean).length < 6
              ) {
                return;
              }
              const candidate = finalCandidate || interimCandidate;
              if (!candidate.trim()) {
                return;
              }
              if (finalCandidate) {
                const finalWords = finalCandidate
                  .split(/\s+/)
                  .filter(Boolean).length;
                const stableForMs =
                  Date.now() - finalTranscriptLastChangedAtRef.current;
                if (stableForMs < computeFinalStabilityMs(finalWords)) {
                  return;
                }
              }
              manualStopRef.current = true;
              shouldKeepListeningRef.current = false;
              recognitionRef.current?.stop();
              setIsListening(false);
              void sendUserTurn(candidate);
            }, silenceMs);
            if (micLanguageMode === "auto") {
              maybeSwitchRecognitionLocale(combined);
            }
          }
        };
        recognition.onerror = () => {
          logSpeakingTelemetry("stt_error");
          clearSilenceTimer();
          clearOnEndTimer();
          clearRetryTimer();
          setIsListening(false);
          localeSwitchInFlightRef.current = false;
          manualStopRef.current = false;
          shouldKeepListeningRef.current = false;
          setMicError(
            translate("speaking_practice_mic_error") ||
              "Microphone could not start. Please check browser microphone permission.",
          );
        };
        recognition.onend = () => {
          logSpeakingTelemetry("stt_end");
          clearSilenceTimer();
          clearOnEndTimer();
          setIsListening(false);
          const stoppedManually = manualStopRef.current;
          manualStopRef.current = false;
          // When recognition ends unexpectedly, give a short grace window
          // to avoid cutting off users who are pausing to think.
          const finalCandidate = finalTranscriptRef.current.trim();
          const interimCandidate = inputRef.current.trim();
          const candidate = finalCandidate || interimCandidate;
          const words = candidate.trim().split(/\s+/).filter(Boolean).length;
          if (candidate && !isPendingRef.current && !stoppedManually) {
            onEndTimerRef.current = window.setTimeout(() => {
              if (isListeningRef.current || isPendingRef.current) {
                return;
              }
              if (finalCandidate) {
                const finalWords = finalCandidate
                  .split(/\s+/)
                  .filter(Boolean).length;
                const stableForMs =
                  Date.now() - finalTranscriptLastChangedAtRef.current;
                if (stableForMs < computeFinalStabilityMs(finalWords)) {
                  return;
                }
              } else {
                // Some browsers end recognition without final chunks.
                // Accept a short interim fallback so turns are not lost.
                if (words < 2) {
                  return;
                }
              }
              void sendUserTurn(candidate);
            }, computeOnEndGraceMs(words));
          }
          const shouldRetryForLocaleSwitch = localeSwitchInFlightRef.current;
          localeSwitchInFlightRef.current = false;
          if (
            (shouldKeepListeningRef.current || shouldRetryForLocaleSwitch) &&
            micMode === "toggle" &&
            !isPendingRef.current
          ) {
            clearRetryTimer();
            retryTimerRef.current = window.setTimeout(() => {
              if (!shouldKeepListeningRef.current || isPendingRef.current) {
                return;
              }
              try {
                recognitionRef.current?.start();
                setIsListening(true);
                playWalkingCue("listening");
                pulseWalkingFeedback(30);
              } catch {
                // noop - browser may reject rapid restart
              }
            }, 300);
          }
        };
        recognitionRef.current = recognition;
        setMicApiSupported(true);
      } catch {
        setMicApiSupported(false);
        logSpeakingTelemetry("stt_ctor_failed");
      }
    } else {
      setMicApiSupported(false);
      logSpeakingTelemetry("stt_missing_api");
    }
    return () => {
      recognitionRef.current?.stop();
      clearSilenceTimer();
      clearOnEndTimer();
      clearRetryTimer();
      if (audioRef.current) {
        audioRef.current.pause();
      }
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
      }
      if ("speechSynthesis" in window) {
        window.speechSynthesis.cancel();
      }
    };
  }, [appLanguage, isDialogueRouteActive, languageSpace, micLanguageMode]);

  useEffect(() => {
    if (!isDialogueRouteActive) {
      return;
    }
    if (messages.length > 0) {
      return;
    }
    const triggerWord = activeTrigger?.word?.trim() || "";
    const triggerMnemonic = activeTrigger?.mnemonic?.trim() || "";
    if (triggerWord) {
      const reason = activeTrigger?.reason || "";
      const triggerWelcomeByLanguage: Record<string, string> = {
        ENGLISH:
          reason === "didnt_know"
            ? `Let's work on "${triggerWord}" now. I can explain how it's used, then you try one sentence.`
            : reason === "known_word"
              ? `Great, you already knew "${triggerWord}". Want a quick usage challenge?`
              : reason === "mnemonic_saved"
                ? `Nice mnemonic for "${triggerWord}". Want to test it in a real sentence now?`
                : `You added "${triggerWord}". Want me to show how it's used in real conversation?`,
        FRENCH:
          reason === "didnt_know"
            ? `On travaille "${triggerWord}" maintenant ? Je t'explique l'usage, puis tu fais une phrase.`
            : reason === "known_word"
              ? `Super, tu connaissais déjà "${triggerWord}". Tu veux un mini défi d'usage ?`
              : reason === "mnemonic_saved"
                ? `Ton moyen mnémotechnique pour "${triggerWord}" est prêt. On le teste dans une phrase ?`
                : `Tu as ajouté "${triggerWord}". Tu veux que je te montre comment l'utiliser en conversation ?`,
        GERMAN:
          reason === "didnt_know"
            ? `Lass uns jetzt "${triggerWord}" üben. Ich erkläre kurz die Verwendung, dann versuchst du einen Satz.`
            : reason === "known_word"
              ? `Stark, du kanntest "${triggerWord}" schon. Lust auf eine kurze Verwendungs-Challenge?`
              : reason === "mnemonic_saved"
                ? `Dein Merksatz zu "${triggerWord}" ist gespeichert. Wollen wir ihn direkt mit einem Satz testen?`
                : `Du hast "${triggerWord}" neu hinzugefügt. Soll ich dir zeigen, wie man es in echten Gesprächen nutzt?`,
      };
      const triggerWelcome =
        triggerWelcomeByLanguage[languageSpace] ||
        `Let's work on "${triggerWord}" now. I can explain how it's used, then you try one sentence.`;
      const withMnemonic = triggerMnemonic
        ? `${triggerWelcome} (${triggerMnemonic})`
        : triggerWelcome;
      setMessages([{ role: "assistant", text: withMnemonic }]);
      if (!hasWelcomedRef.current && autoVoiceEnabled) {
        hasWelcomedRef.current = true;
        void speakText(withMnemonic, { suppressErrorUi: true });
      }
      return;
    }

    const welcomeByLanguage: Record<string, string> = {
      ENGLISH: "Hi. We can start now. How is your day going?",
      FRENCH: "Salut, ça va ?",
      GERMAN: "Hallo. Wir können jetzt sprechen. Wie läuft dein Tag?",
      SPANISH: "Hola. Podemos hablar ahora. ¿Cómo va tu día?",
      ITALIAN: "Ciao. Possiamo parlare adesso. Com'è la tua giornata?",
      PORTUGUESE: "Olá. Podemos conversar agora. Como está seu dia?",
      PORTUGUESE_FROM_PORTUGAL:
        "Olá. Podemos conversar agora. Como está o teu dia?",
      PORTUGUESE_FROM_BRAZIL:
        "Olá. Podemos conversar agora. Como está seu dia?",
      RUSSIAN: "Привет. Можем поговорить сейчас. Как проходит твой день?",
      GREEK: "Γεια. Μπορούμε να μιλήσουμε τώρα. Πώς πάει η μέρα σου;",
    };
    const welcome =
      welcomeByLanguage[languageSpace] ||
      translate("speaking_practice_welcome") ||
      "Salut, ça va ?";
    setMessages([{ role: "assistant", text: welcome }]);
    if (!hasWelcomedRef.current && autoVoiceEnabled) {
      hasWelcomedRef.current = true;
      void speakText(welcome, { suppressErrorUi: true });
    }
  }, [
    activeTrigger,
    autoVoiceEnabled,
    isDialogueRouteActive,
    languageSpace,
    messages.length,
    translate,
  ]);

  useEffect(() => {
    if (
      !isDialogueRouteActive ||
      !handsFreeMode ||
      !hasMicPermissionRef.current
    ) {
      return;
    }
    if (isListening || isSpeaking || replyMutation.isPending) {
      return;
    }
    const timer = window.setTimeout(() => {
      startListeningIfPossible();
    }, 60);
    return () => window.clearTimeout(timer);
  }, [
    handsFreeMode,
    isDialogueRouteActive,
    isListening,
    isSpeaking,
    replyMutation.isPending,
  ]);

  useEffect(() => {
    if (!walkingModeEnabled || !isDialogueRouteActive) {
      return;
    }
    setAutoVoiceEnabled(true);
    setHandsFreeMode(true);
    setMicMode("toggle");
    setMicLanguageMode("auto");
    setPauseSensitivity("patient");
    setIsTextInputOpen(false);
    shouldKeepListeningRef.current = true;
    if (
      !isListening &&
      !isSpeaking &&
      !replyMutation.isPending &&
      hasMicPermissionRef.current
    ) {
      startListeningIfPossible();
    }
  }, [
    isDialogueRouteActive,
    isListening,
    isSpeaking,
    replyMutation.isPending,
    setAutoVoiceEnabled,
    walkingModeEnabled,
  ]);

  useEffect(() => {
    const recognition = recognitionRef.current;
    if (!recognition) {
      return;
    }
    if (micLanguageMode === "german") {
      setRecognitionLanguageSpace("GERMAN");
    } else if (micLanguageMode === "learning") {
      setRecognitionLanguageSpace(languageSpace);
    } else if (!activeRecognitionLanguageRef.current) {
      setRecognitionLanguageSpace(languageSpace);
    }
    recognition.lang = resolveMicLocale();
  }, [appLanguage, languageSpace, micLanguageMode]);

  const toggleListening = async () => {
    if (!isDialogueRouteActive) {
      return;
    }
    const recognition = recognitionRef.current;
    if (isListening) {
      manualStopRef.current = true;
      shouldKeepListeningRef.current = false;
      recognition?.stop();
      clearSilenceTimer();
      clearOnEndTimer();
      setIsListening(false);
      setMicError("");
      return;
    }
    setMicError("");
    try {
      if (
        typeof navigator === "undefined" ||
        !navigator.mediaDevices ||
        !navigator.mediaDevices.getUserMedia
      ) {
        throw new Error("getUserMedia-unavailable");
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      hasMicPermissionRef.current = true;
      logSpeakingTelemetry("mic_permission_granted_runtime");

      if (!recognition) {
        playWalkingCue("error");
        pulseWalkingFeedback([40, 40, 40]);
        setMicError(
          translate("speaking_practice_mic_unsupported") ||
            "Speech-to-text is not available in this browser. Please use current Chrome or Safari.",
        );
        return;
      }
      finalTranscriptRef.current = "";
      finalTranscriptByIndexRef.current = {};
      manualStopRef.current = false;
      shouldKeepListeningRef.current = true;
      recognition.start();
      setIsListening(true);
      playWalkingCue("listening");
      pulseWalkingFeedback(30);
    } catch {
      setIsListening(false);
      logSpeakingTelemetry("mic_start_failed");
      playWalkingCue("error");
      pulseWalkingFeedback([40, 40, 40]);
      setMicError(
        translate("speaking_practice_mic_error") ||
          "Microphone could not start. Please check browser microphone permission.",
      );
    }
  };

  const startWalkingMode = async () => {
    walkingModeEnabledRef.current = true;
    setWalkingModeEnabled(true);
    setAutoVoiceEnabled(true);
    setHandsFreeMode(true);
    setMicMode("toggle");
    setMicLanguageMode("auto");
    setPauseSensitivity("patient");
    setIsTextInputOpen(false);
    setMicError("");
    shouldKeepListeningRef.current = true;
    void requestWalkingWakeLock();

    if (isListeningRef.current || isPendingRef.current || isSpeaking) {
      return;
    }

    const recognition = recognitionRef.current;
    try {
      if (
        typeof navigator === "undefined" ||
        !navigator.mediaDevices ||
        !navigator.mediaDevices.getUserMedia
      ) {
        throw new Error("getUserMedia-unavailable");
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      hasMicPermissionRef.current = true;

      if (!recognition) {
        playWalkingCue("error");
        pulseWalkingFeedback([40, 40, 40]);
        setMicError(
          translate("speaking_practice_mic_unsupported") ||
            "Speech-to-text is not available in this browser. Please use current Chrome or Safari.",
        );
        return;
      }

      finalTranscriptRef.current = "";
      finalTranscriptByIndexRef.current = {};
      manualStopRef.current = false;
      setRecognitionLanguageSpace(languageSpace);
      recognition.lang = resolveMicLocale();
      recognition.start();
      setIsListening(true);
      playWalkingCue("listening");
      pulseWalkingFeedback(30);
      logSpeakingTelemetry("walking_mode_started");
    } catch {
      setIsListening(false);
      playWalkingCue("error");
      pulseWalkingFeedback([40, 40, 40]);
      logSpeakingTelemetry("walking_mode_start_failed");
      setMicError(
        translate("speaking_practice_mic_error") ||
          "Microphone could not start. Please check browser microphone permission.",
      );
    }
  };

  const stopWalkingMode = () => {
    walkingModeEnabledRef.current = false;
    setWalkingModeEnabled(false);
    shouldKeepListeningRef.current = false;
    clearSilenceTimer();
    clearOnEndTimer();
    clearRetryTimer();
    recognitionRef.current?.stop();
    setIsListening(false);
    releaseWalkingWakeLock();
    logSpeakingTelemetry("walking_mode_stopped");
  };

  const toggleWalkingMode = async () => {
    if (walkingModeEnabledRef.current) {
      stopWalkingMode();
      return;
    }
    await startWalkingMode();
  };

  const stopSpeaking = () => {
    if (typeof window === "undefined") {
      return;
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }
    if ("speechSynthesis" in window) {
      window.speechSynthesis.cancel();
    }
    setIsSpeaking(false);
  };

  const toggleCueSnoozeForSession = () => {
    if (typeof window === "undefined") {
      return;
    }
    const next = !cueSnoozedForSession;
    if (next) {
      window.sessionStorage.setItem("speaking-cue-snooze-session-v1", "1");
    } else {
      window.sessionStorage.removeItem("speaking-cue-snooze-session-v1");
    }
    setCueSnoozedForSession(next);
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = input.trim();
    if (!trimmed) {
      return;
    }

    if (isListening) {
      recognitionRef.current?.stop();
      clearSilenceTimer();
      clearOnEndTimer();
      clearRetryTimer();
      shouldKeepListeningRef.current = false;
      setIsListening(false);
    }
    await sendUserTurn(trimmed);
  };

  const replayLastAssistantVoice = () => {
    if (pendingVoiceReplayText.trim()) {
      void speakText(pendingVoiceReplayText);
      return;
    }
    const lastAssistant = [...messages]
      .reverse()
      .find((message) => message.role === "assistant" && message.text.trim());
    if (!lastAssistant) {
      return;
    }
    void speakText(lastAssistant.text);
  };

  return (
    <div className="fixed inset-0 z-[120] overflow-hidden bg-[#f7f7f4] p-0 md:p-4">
      <div className="relative mx-auto flex h-full w-full max-w-3xl flex-col border border-stone-200 bg-white p-4 md:rounded-lg md:p-5">
        <div className="mb-4 flex items-center justify-between gap-3">
          <h1 className="text-base font-semibold text-stone-950">
            {translate("speaking_practice_title") || "Speaking practice"}
          </h1>
          <button
            type="button"
            onClick={() => {
              if (typeof window !== "undefined" && window.history.length > 1) {
                window.history.back();
                return;
              }
              void navigate({
                to: "/app/reeprise",
                search: { mobileView: undefined },
              });
            }}
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-stone-200 bg-white text-stone-700 transition hover:bg-stone-50"
            aria-label={translate("close_button") || "Close"}
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="rounded-lg border border-stone-200 bg-[#fafafa] p-3">
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => void toggleWalkingMode()}
              className={`inline-flex min-h-11 items-center gap-2 rounded-lg border px-4 py-2.5 text-base font-semibold transition ${
                walkingModeEnabled
                  ? "border-[#10a37f] bg-[#10a37f] text-white"
                  : "border-stone-300 bg-white text-stone-950 hover:bg-stone-50"
              }`}
              aria-pressed={walkingModeEnabled}
            >
              <MicrophoneIcon className="h-5 w-5" />
              {walkingModeEnabled
                ? translate("speaking_practice_walking_mode_on") ||
                  "Walking mode: on"
                : translate("speaking_practice_walking_mode_off") ||
                  "Walking mode"}
            </button>
            <p
              className="rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm font-medium text-stone-700"
              aria-live="polite"
            >
              {replyMutation.isPending
                ? translate("speaking_practice_walking_status_replying") ||
                  "Replying"
                : isSpeaking
                  ? translate("speaking_practice_walking_status_speaking") ||
                    "Speaking"
                  : isListening
                    ? translate("speaking_practice_walking_status_listening") ||
                      "Listening"
                    : walkingModeEnabled
                      ? translate("speaking_practice_walking_status_ready") ||
                        "Ready"
                      : translate("speaking_practice_walking_status_manual") ||
                        "Manual"}
            </p>
          </div>
        </div>

        {replyMutation.isPending ? (
          <div className="mt-3 rounded-lg border border-stone-200 bg-white px-4 py-3">
            <div className="speaking-aura mx-auto" aria-hidden="true">
              <div className="speaking-aura-ring speaking-aura-ring-a" />
              <div className="speaking-aura-ring speaking-aura-ring-b" />
              <div className="speaking-aura-core" />
            </div>
          </div>
        ) : null}

        {voiceError ? (
          <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-stone-950">
            <p className="text-base font-semibold">
              {translate("speaking_practice_voice_status_title") ||
                "Voice could not start"}
            </p>
            <p className="mt-1 text-sm">{voiceError}</p>
            <p className="mt-2 text-sm font-semibold">
              {translate("speaking_practice_voice_status_what_to_do") ||
                "What you can do now"}
            </p>
            <ul className="mt-1 list-disc space-y-1 pl-5 text-sm">
              <li>
                {translate("speaking_practice_voice_status_step_retry") ||
                  "Tap Retry voice."}
              </li>
              <li>
                {translate("speaking_practice_voice_status_step_network") ||
                  "Check your connection and try again."}
              </li>
              <li>
                {translate("speaking_practice_voice_status_step_continue") ||
                  "Continue chatting by text or microphone while voice is unavailable."}
              </li>
            </ul>
            <button
              type="button"
              onClick={replayLastAssistantVoice}
              className="mt-3 inline-flex items-center gap-2 rounded-lg border border-amber-400 bg-white px-3 py-2 text-base font-semibold text-stone-950"
            >
              <SpeakerWaveIcon className="h-5 w-5" />
              {translate("speaking_practice_voice_retry") || "Retry voice"}
            </button>
          </div>
        ) : null}

        <div
          ref={transcriptViewportRef}
          className="mt-4 min-h-0 flex-1 overflow-y-auto pr-1 pb-6"
        >
          <div className="space-y-3">
            {messages.map((message, index) => (
              <div
                key={`${message.role}-${index}`}
                className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[88%] rounded-lg border px-4 py-3 ${
                    message.role === "user"
                      ? "border-stone-300 bg-white text-stone-950"
                      : "border-stone-200 bg-[#f7f7f4] text-stone-900"
                  }`}
                >
                  <p className="text-base leading-relaxed">{message.text}</p>
                  {message.role === "user" && message.correction ? (
                    <div className="mt-2">
                      <button
                        type="button"
                        onClick={() =>
                          setExpandedCorrections((prev) => ({
                            ...prev,
                            [index]: !prev[index],
                          }))
                        }
                        className="text-sm font-semibold text-stone-700 underline underline-offset-2"
                      >
                        {expandedCorrections[index]
                          ? translate("speaking_practice_hide_correction") ||
                            "Hide correction"
                          : translate("speaking_practice_show_correction") ||
                            "Show correction"}
                      </button>
                      {expandedCorrections[index] ? (
                        <p className="mt-2 text-sm leading-relaxed text-stone-700">
                          <span className="font-semibold">
                            {translate("speaking_practice_correction") ||
                              "Correction hint"}
                            :
                          </span>{" "}
                          {message.correction}
                        </p>
                      ) : null}
                    </div>
                  ) : null}
                  {message.role === "assistant" &&
                  message.coaching &&
                  (message.coaching.better_phrasing ||
                    message.coaching.pronunciation_focus ||
                    message.coaching.new_word) ? (
                    <div className="mt-3 border-t border-stone-200 pt-3 text-sm leading-relaxed text-stone-700">
                      <p className="font-semibold">
                        {translate("speaking_practice_coaching_title") ||
                          "Quick coaching"}
                      </p>
                      {message.coaching.better_phrasing ? (
                        <p className="mt-1">
                          <span className="font-semibold">
                            {translate(
                              "speaking_practice_coaching_better_phrasing",
                            ) || "Better phrasing"}
                            :
                          </span>{" "}
                          {message.coaching.better_phrasing}
                        </p>
                      ) : null}
                      {message.coaching.pronunciation_focus ? (
                        <p className="mt-1">
                          <span className="font-semibold">
                            {translate(
                              "speaking_practice_coaching_pronunciation_focus",
                            ) || "Pronunciation focus"}
                            :
                          </span>{" "}
                          {message.coaching.pronunciation_focus}
                        </p>
                      ) : null}
                      {message.coaching.new_word ? (
                        <p className="mt-1">
                          <span className="font-semibold">
                            {translate("speaking_practice_coaching_new_word") ||
                              "New word"}
                            :
                          </span>{" "}
                          {message.coaching.new_word}
                        </p>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>

        <form
          onSubmit={(event) => void onSubmit(event)}
          className="mt-4 space-y-3"
        >
          {isTextInputOpen ? (
            <div className="space-y-2">
              <textarea
                id="speaking-practice-message"
                value={input}
                onChange={(event) => setInput(event.target.value)}
                rows={3}
                className="w-full rounded-lg border border-stone-300 bg-white px-4 py-3 text-base text-stone-950 outline-none transition focus:border-stone-500"
                placeholder={
                  translate("speaking_practice_input_placeholder") ||
                  "Write what you would say in a real conversation."
                }
              />
              <button
                type="button"
                onClick={() => setIsTextInputOpen(false)}
                className="text-sm font-semibold text-stone-700 underline underline-offset-2"
              >
                {translate("speaking_practice_close_write") || "Close writing"}
              </button>
            </div>
          ) : null}
          <div className="flex flex-wrap items-center gap-2 border-t border-stone-200 pt-3">
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => {
                  if (micMode === "toggle") {
                    void toggleListening();
                  }
                }}
                onMouseDown={() => {
                  if (micMode === "hold" && !isListening) {
                    void toggleListening();
                  }
                }}
                onMouseUp={() => {
                  if (micMode === "hold" && isListening) {
                    void toggleListening();
                  }
                }}
                onTouchStart={() => {
                  if (micMode === "hold" && !isListening) {
                    void toggleListening();
                  }
                }}
                onTouchEnd={() => {
                  if (micMode === "hold" && isListening) {
                    void toggleListening();
                  }
                }}
                disabled={replyMutation.isPending || !micApiSupported}
                className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-stone-300 bg-white px-3 py-2 text-base font-semibold text-stone-950 transition hover:bg-stone-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <MicrophoneIcon className="h-5 w-5" />
                {micMode === "hold"
                  ? translate("speaking_practice_hold_to_talk") ||
                    "Hold to talk"
                  : isListening
                    ? translate("speaking_practice_stop_mic") || "Stop mic"
                    : translate("speaking_practice_start_mic") || "Start mic"}
              </button>
              <button
                type="button"
                onClick={() => setIsTextInputOpen((prev) => !prev)}
                className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-stone-300 bg-white px-3 py-2 text-base font-semibold text-stone-950 transition hover:bg-stone-50"
              >
                <PencilSquareIcon className="h-5 w-5" />
                {isTextInputOpen
                  ? translate("speaking_practice_write_open") || "Write: open"
                  : translate("speaking_practice_write_open") || "Write"}
              </button>
              <button
                type="button"
                onClick={() => setShowVoiceSettings((prev) => !prev)}
                className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-stone-300 bg-white px-3 py-2 text-base font-semibold text-stone-950 transition hover:bg-stone-50"
              >
                <Cog6ToothIcon className="h-5 w-5" />
                {showVoiceSettings
                  ? translate("speaking_practice_hide_settings") ||
                    "Settings ausblenden"
                  : translate("speaking_practice_show_settings") || "Settings"}
              </button>
            </div>
          </div>
          {showVoiceSettings ? (
            <div className="space-y-3 rounded-lg border border-stone-200 bg-[#fafafa] p-3">
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={stopSpeaking}
                  disabled={!isSpeaking}
                  className="inline-flex min-h-10 items-center gap-2 rounded-lg border border-stone-300 bg-white px-3 py-2 text-base font-semibold text-stone-950 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <SpeakerWaveIcon className="h-5 w-5" />
                  {translate("speaking_practice_stop_voice") || "Stop voice"}
                </button>
                <button
                  type="button"
                  onClick={() => setAutoVoiceEnabled((prev) => !prev)}
                  className="rounded-lg border border-stone-300 bg-white px-3 py-2 text-base font-semibold text-stone-950 transition hover:bg-stone-50"
                >
                  {autoVoiceEnabled
                    ? translate("speaking_practice_auto_voice_on") ||
                      "Auto voice: on"
                    : translate("speaking_practice_auto_voice_off") ||
                      "Auto voice: off"}
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setVoiceSpeed((prev) =>
                      prev === "normal" ? "slow" : "normal",
                    )
                  }
                  className="rounded-lg border border-stone-300 bg-white px-3 py-2 text-base font-semibold text-stone-950 transition hover:bg-stone-50"
                >
                  {voiceSpeed === "slow"
                    ? translate("speaking_practice_voice_speed_slow") ||
                      "Voice: slow"
                    : translate("speaking_practice_voice_speed_normal") ||
                      "Voice: normal"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const next = !coachingEnabledRef.current;
                    coachingEnabledRef.current = next;
                    setCoachingEnabled(next);
                  }}
                  className="rounded-lg border border-stone-300 bg-white px-3 py-2 text-base font-semibold text-stone-950 transition hover:bg-stone-50"
                >
                  {translate("speaking_practice_coaching_toggle_label") ||
                    "Turn-level coaching"}
                  :{" "}
                  {coachingEnabled
                    ? translate("speaking_practice_toggle_on") || "On"
                    : translate("speaking_practice_toggle_off") || "Off"}
                </button>
                {isIOSDevice ? (
                  <p className="w-full text-sm text-stone-600">
                    {translate("speaking_practice_ios_audio_hint") ||
                      "On iPhone, automatic voice can trigger output-device switching. Keep Auto voice off if this is distracting."}
                  </p>
                ) : null}
              </div>
              <div className="flex flex-wrap gap-2 border-t border-stone-200 pt-3">
                <button
                  type="button"
                  onClick={() =>
                    setMicMode((prev) =>
                      prev === "toggle" ? "hold" : "toggle",
                    )
                  }
                  className="rounded-lg border border-stone-300 bg-white px-3 py-2 text-base font-semibold text-stone-950 transition hover:bg-stone-50"
                >
                  {micMode === "hold"
                    ? translate("speaking_practice_mic_mode_hold") ||
                      "Mic: hold"
                    : translate("speaking_practice_mic_mode_toggle") ||
                      "Mic: tap"}
                </button>
                <button
                  type="button"
                  onClick={() => setHandsFreeMode((prev) => !prev)}
                  className="rounded-lg border border-stone-300 bg-white px-3 py-2 text-base font-semibold text-stone-950 transition hover:bg-stone-50"
                >
                  {handsFreeMode
                    ? translate("speaking_practice_hands_free_on") ||
                      "Hands-free: on"
                    : translate("speaking_practice_hands_free_off") ||
                      "Hands-free: off"}
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setPauseSensitivity((prev) =>
                      prev === "fast"
                        ? "balanced"
                        : prev === "balanced"
                          ? "patient"
                          : "fast",
                    )
                  }
                  className="rounded-lg border border-stone-300 bg-white px-3 py-2 text-base font-semibold text-stone-950 transition hover:bg-stone-50"
                >
                  {pauseSensitivity === "fast"
                    ? translate("speaking_practice_pause_fast") || "Pause: fast"
                    : pauseSensitivity === "patient"
                      ? translate("speaking_practice_pause_patient") ||
                        "Pause: patient"
                      : translate("speaking_practice_pause_balanced") ||
                        "Pause: balanced"}
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setMicLanguageMode((prev) =>
                      prev === "auto"
                        ? "german"
                        : prev === "german"
                          ? "learning"
                          : "auto",
                    )
                  }
                  className="rounded-lg border border-stone-300 bg-white px-3 py-2 text-base font-semibold text-stone-950 transition hover:bg-stone-50"
                >
                  {micLanguageMode === "german"
                    ? translate("speaking_practice_mic_lang_german") ||
                      "Mic: Deutsch"
                    : micLanguageMode === "learning"
                      ? translate("speaking_practice_mic_lang_learning") ||
                        "Mic: Lernsprache"
                      : translate("speaking_practice_mic_lang_auto") ||
                        "Mic: Auto"}
                </button>
                <button
                  type="submit"
                  disabled={
                    !isTextInputOpen ||
                    input.trim().length === 0 ||
                    replyMutation.isPending
                  }
                  className="rounded-lg border border-stone-300 bg-white px-4 py-2 text-base font-semibold text-stone-950 transition hover:bg-stone-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {translate("speaking_practice_send") || "Send"}
                </button>
              </div>
              <div className="flex w-full flex-wrap items-center gap-2 border-t border-stone-200 pt-3">
                <span className="text-sm font-semibold text-stone-700">
                  {translate("speaking_practice_cue_mode_label") || "Cue"}
                </span>
                <button
                  type="button"
                  onClick={() => setSpeakingCueMode("off")}
                  className="rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm font-semibold text-stone-950 transition hover:bg-stone-50"
                  aria-pressed={speakingCueMode === "off"}
                >
                  {translate("speaking_practice_cue_mode_off") || "Off"}
                </button>
                <button
                  type="button"
                  onClick={() => setSpeakingCueMode("subtle")}
                  className="rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm font-semibold text-stone-950 transition hover:bg-stone-50"
                  aria-pressed={speakingCueMode === "subtle"}
                >
                  {translate("speaking_practice_cue_mode_subtle") || "Subtle"}
                </button>
                <button
                  type="button"
                  onClick={() => setSpeakingCueMode("prominent")}
                  className="rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm font-semibold text-stone-950 transition hover:bg-stone-50"
                  aria-pressed={speakingCueMode === "prominent"}
                >
                  {translate("speaking_practice_cue_mode_prominent") ||
                    "Prominent"}
                </button>
                <button
                  type="button"
                  onClick={toggleCueSnoozeForSession}
                  className="rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm font-semibold text-stone-950 transition hover:bg-stone-50"
                >
                  {cueSnoozedForSession
                    ? translate("speaking_practice_cue_snooze_off") ||
                      "Cue this session: on"
                    : translate("speaking_practice_cue_snooze_on") ||
                      "Cue this session: off"}
                </button>
              </div>
            </div>
          ) : null}
          {!micApiSupported ? (
            <p className="text-sm text-stone-600">
              {isFirefox
                ? translate("speaking_practice_mic_browser_hint_firefox") ||
                  "Firefox currently supports typing here, but live microphone dictation is limited. Use Chrome or Safari for full voice conversation."
                : translate("speaking_practice_mic_browser_hint") ||
                  "Voice typing works in current Chrome and Safari versions."}
            </p>
          ) : null}
          {micError ? <p className="text-sm text-red-700">{micError}</p> : null}
          {lastTranscript ? (
            <button
              type="button"
              onClick={() => {
                void sendUserTurn(lastTranscript);
              }}
              className="text-sm font-semibold text-stone-700 underline underline-offset-2"
            >
              {translate("speaking_practice_resend_last") ||
                "Resend last transcript"}
            </button>
          ) : null}
        </form>
      </div>
    </div>
  );
}
